import type { ConnectorRoutingPolicy, ConnectorRoutingPolicyRegistry } from './types';

class InMemoryConnectorRoutingPolicyRegistry implements ConnectorRoutingPolicyRegistry {
  private readonly policies = new Map<string, ConnectorRoutingPolicy>();

  register(policy: ConnectorRoutingPolicy): void {
    this.policies.set(policy.id, policy);
  }

  resolve(policyId: string | undefined): ConnectorRoutingPolicy | undefined {
    if (!policyId) return undefined;
    return this.policies.get(policyId);
  }

  list(): ConnectorRoutingPolicy[] {
    return Array.from(this.policies.values());
  }
}

export const connectorRoutingPolicyRegistry = new InMemoryConnectorRoutingPolicyRegistry();

export const CORE_ROUTING_POLICY_IDS = {
  genericStraight: 'generic-straight',
  genericBezier: 'generic-bezier',
  genericOrthogonal: 'generic-orthogonal',
  genogramStructural: 'genogram-structural',
  genogramRelationshipOverlay: 'genogram-relationship-overlay',
  processFlow: 'process-flow',
  networkTopology: 'network-topology',
} as const;

const GENERIC_POLICIES: ConnectorRoutingPolicy[] = [
  {
    id: CORE_ROUTING_POLICY_IDS.genericStraight,
    family: 'generic-straight',
    defaultRoutingType: 'straight',
    allowedLayoutSources: [],
    layoutPointTrust: 'never',
    obstacleMode: 'none',
    endpointMode: 'free',
    manualOverride: 'allow',
  },
  {
    id: CORE_ROUTING_POLICY_IDS.genericBezier,
    family: 'generic-bezier',
    defaultRoutingType: 'bezier',
    allowedLayoutSources: [],
    layoutPointTrust: 'never',
    obstacleMode: 'none',
    endpointMode: 'free',
    manualOverride: 'allow',
  },
  {
    id: CORE_ROUTING_POLICY_IDS.genericOrthogonal,
    family: 'generic-orthogonal',
    defaultRoutingType: 'orthogonal',
    allowedLayoutSources: [],
    layoutPointTrust: 'validate-visible-geometry',
    obstacleMode: 'visible-shape-interiors',
    endpointMode: 'boundary',
    manualOverride: 'allow',
  },
  {
    id: CORE_ROUTING_POLICY_IDS.genogramStructural,
    family: 'genogram-structural',
    defaultRoutingType: 'straight',
    allowedLayoutSources: [],
    layoutPointTrust: 'never',
    obstacleMode: 'none',
    endpointMode: 'domain-ports',
    manualOverride: 'allow',
  },
  {
    id: CORE_ROUTING_POLICY_IDS.genogramRelationshipOverlay,
    family: 'genogram-relationship-overlay',
    defaultRoutingType: 'bezier',
    allowedLayoutSources: [],
    layoutPointTrust: 'never',
    obstacleMode: 'none',
    endpointMode: 'boundary',
    manualOverride: 'allow',
  },
  {
    id: CORE_ROUTING_POLICY_IDS.processFlow,
    family: 'process-flow',
    defaultRoutingType: 'orthogonalRounded',
    allowedLayoutSources: ['elk', 'process-swimlane'],
    layoutPointTrust: 'validate-visible-geometry',
    obstacleMode: 'visible-shape-interiors',
    endpointMode: 'boundary',
    manualOverride: 'allow',
  },
  {
    id: CORE_ROUTING_POLICY_IDS.networkTopology,
    family: 'network-topology',
    defaultRoutingType: 'orthogonal',
    allowedLayoutSources: ['compass-explorer', 'compose-service'],
    layoutPointTrust: 'validate-visible-geometry',
    obstacleMode: 'visible-shape-interiors',
    endpointMode: 'boundary',
    manualOverride: 'allow',
  },
];

GENERIC_POLICIES.forEach((policy) => connectorRoutingPolicyRegistry.register(policy));
